import { useT } from "@transifex/react";
import type { SchoolClass } from "p6m-p6u";
import { NO_CLASS_SELECTION } from "../../../enums/shareFlow";
import { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import { ClassSelectionCard } from "../../classActivityCard/classTag/ClassSelectionCard";
import { Prohibit } from "@phosphor-icons/react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { useRef, useState } from "react";
import InlineCreateClassForm from "../../schoolClasses/InlineCreateClassForm";
import type { NewClassFormState } from "../../classActivityCard/ClassActivityCardsOverview.types";

export interface ShareClassStepHeaderHint {
    text: string;
    infoExpanded: boolean;
    onToggleInfo: () => void;
    infoLines: string[];
}

interface ShareClassStepProps {
    selectedSchoolClassId: string | null;
    onSelectClass: (schoolClassId: string | null) => void;
    schoolClasses: SchoolClass[];
    classActivityCategory: ClassActivityCategory;

    showNoClassCard: boolean;
    isClassDisabled?: (schoolClass: SchoolClass) => boolean;
    headerHint?: ShareClassStepHeaderHint;

    newClassForm: NewClassFormState | null;
    onOpenNewClassForm: () => void;
    onCloseNewClassForm: () => void;
    onCollapseNewClassForm: () => void;
    onNewClassNameChange: (name: string) => void;
    onToggleNewClassConsent: () => void;
}

const infoPanelStyles: Record<ClassActivityCategory, string> = {
    [ClassActivityCategory.TEST]: "border-p6-orange bg-p6-orange-light-pastel",
    [ClassActivityCategory.VOCAB_TRAINING]: "border-forest-green-800 bg-forest-green-pastel",
};

const activeIconStyles: Record<ClassActivityCategory, string> = {
    [ClassActivityCategory.TEST]: "text-p6-orange",
    [ClassActivityCategory.VOCAB_TRAINING]: "text-forest-green-800",
};

const INITIAL_VISIBLE_COUNT = 6;

const ShareClassStep = (props: ShareClassStepProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const [isExpanded, setIsExpanded] = useState(false);

    // Only sort pre-assigned/inter-step selection to top — not in-step clicks
    const initialSelectedSchoolClassId = useRef(props.selectedSchoolClassId);

    const t_heading = t("Select your class:", { _tags: "ShareFlow, ShareClassStep" });
    const t_newClass = t("Create new private class", { _tags: "ShareFlow, ShareClassStep" });
    const t_newClassSubtitle = t("Access only for students", { _tags: "ShareFlow, ShareClassStep" });
    const t_noClass = t("Select no class", { _tags: "ShareFlow, ShareClassStep" });
    const t_noClassSubtitle = t("(Share link only)", { _tags: "ShareFlow, ShareClassStep" });
    const t_public = t("Public", { _tags: "ShareFlow, ShareClassStep" });
    const t_private = t("Private", { _tags: "ShareFlow, ShareClassStep" });
    const t_showMore = t("show more", { _tags: "ShareFlow, ShareClassStep" });
    const t_showLess = t("show less", { _tags: "ShareFlow, ShareClassStep" });

    const specialCardCount = props.showNoClassCard ? 2 : 1; // "New class" always, "No class" optional
    const classVisibleCount = INITIAL_VISIBLE_COUNT - specialCardCount;

    const pinnedSchoolClassId = initialSelectedSchoolClassId.current;
    const sortedClasses = [...props.schoolClasses].sort((a, b) => {
        if (pinnedSchoolClassId && a.token === pinnedSchoolClassId) return -1;
        if (pinnedSchoolClassId && b.token === pinnedSchoolClassId) return 1;
        return b.dateInserted - a.dateInserted;
    });

    const hasMoreClasses = sortedClasses.length > classVisibleCount;
    const visibleClasses = isExpanded ? sortedClasses : sortedClasses.slice(0, classVisibleCount);

    const getClassVisibilityLabel = (classVisibility: SchoolClassVisibilities) => {
        return classVisibility === SchoolClassVisibilities.PUBLIC ? t_public : t_private;
    };

    const isClassDisabled = (schoolClass: SchoolClass) => props.isClassDisabled?.(schoolClass) ?? false;

    const handleToggleExpanded = () => setIsExpanded((prev) => !prev);

    const renderNewClassCard = () => {
        const form = props.newClassForm;
        const pendingCommittedName = form && !form.isOpen && form.isCommitted ? form.name : null;
        return (
            <ClassSelectionCard
                title={pendingCommittedName ?? t_newClass}
                subtitle={t_newClassSubtitle}
                icon="add"
                isSelected={pendingCommittedName !== null}
                isDashed
                isDisabled={false}
                classActivityCategory={props.classActivityCategory}
                onClick={props.onOpenNewClassForm}
            />
        );
    };

    const renderNoClassCard = () => {
        if (!props.showNoClassCard) return null;
        const isSelected = props.selectedSchoolClassId === NO_CLASS_SELECTION;
        return (
            <ClassSelectionCard
                title={t_noClass}
                subtitle={t_noClassSubtitle}
                phosphorIcon={Prohibit}
                isSelected={isSelected}
                isDashed={false}
                isDisabled={false}
                classActivityCategory={props.classActivityCategory}
                onClick={() => props.onSelectClass(NO_CLASS_SELECTION)}
            />
        );
    };

    const renderClassCards = () => {
        return visibleClasses.map((schoolClass) => (
            <ClassSelectionCard
                key={schoolClass.token}
                data-testid={`share-class-option-${schoolClass.token}`}
                title={schoolClass.name}
                subtitle={getClassVisibilityLabel(schoolClass.classVisibility)}
                icon="group"
                isSelected={props.selectedSchoolClassId === schoolClass.token}
                isDashed={false}
                isDisabled={isClassDisabled(schoolClass)}
                classActivityCategory={props.classActivityCategory}
                onClick={() => props.onSelectClass(schoolClass.token)}
            />
        ));
    };

    const renderShowMoreToggle = () => {
        if (!hasMoreClasses) return null;
        return (
            <button
                type="button"
                onClick={handleToggleExpanded}
                className="flex items-center justify-center gap-xs text-l text-p6-grey-dark cursor-pointer"
            >
                <span>{isExpanded ? t_showLess : t_showMore}</span>
                <PhaseSixIcon
                    name="chevron-down"
                    className={`text-xxl transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>
        );
    };

    const renderInfoPanel = () => {
        if (!props.headerHint?.infoExpanded) return null;
        return (
            <div className={`border-l-4 ${infoPanelStyles[props.classActivityCategory]} px-m py-xs`}>
                {props.headerHint.infoLines.map((line) => (
                    <p
                        key={line}
                        className="text-m text-p6-grey-dark"
                    >
                        {line}
                    </p>
                ))}
            </div>
        );
    };

    const renderHeader = () => (
        <div className="flex flex-col gap-xs">
            <p className="text-l font-semibold text-p6-grey-dark">{t_heading}</p>
            {props.headerHint && (
                <>
                    <div className="flex gap-xs items-center">
                        <p className="text-l text-p6-grey-dark">{props.headerHint.text}</p>
                        <button
                            type="button"
                            onClick={props.headerHint.onToggleInfo}
                            className="cursor-pointer shrink-0"
                        >
                            <PhaseSixIcon
                                name="help"
                                className={`text-xxl ${
                                    props.headerHint.infoExpanded
                                        ? activeIconStyles[props.classActivityCategory]
                                        : "text-p6-grey-dark"
                                }`}
                            />
                        </button>
                    </div>
                    {renderInfoPanel()}
                </>
            )}
        </div>
    );

    if (props.newClassForm?.isOpen) {
        const existingClassNames = props.schoolClasses.map((c) => c.name);
        return (
            <div className="flex flex-col gap-m w-full">
                {renderHeader()}

                <InlineCreateClassForm
                    name={props.newClassForm.name}
                    hasConsent={props.newClassForm.hasConsent}
                    existingClassNames={existingClassNames}
                    onNameChange={props.onNewClassNameChange}
                    onConsentToggle={props.onToggleNewClassConsent}
                    onClose={props.onCloseNewClassForm}
                    onCollapse={props.onCollapseNewClassForm}
                />
            </div>
        );
    }

    return (
        <div
            className="flex flex-col gap-m w-full"
            data-testid="share-class-step"
        >
            {renderHeader()}

            <div className={`grid gap-s ${getStyleBasedOnBreakpoint(Breakpoint.SM, "grid-cols-2", "grid-cols-1")}`}>
                {renderNewClassCard()}
                {renderNoClassCard()}
                {renderClassCards()}
            </div>

            {renderShowMoreToggle()}
        </div>
    );
};

export default ShareClassStep;
